import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { HeartPulse, Download, FileText, Copy, CheckCircle2, FileDown, Globe } from "lucide-react";
import { toast } from "sonner";

const VERSIONS = [
  { key: "pilote", label: "Dossier technique & financier (pilote 41 400 USD)", desc: "Réponse complète au bailleur : budget détaillé, calendrier 3 mois, résultats attendus, suivi-évaluation", flag: "📑" },
  { key: "pilote_en", label: "Technical & Financial Proposal (EN, pilot USD 41,400)", desc: "English version of the full funder response: detailed budget, 3-month timeline, expected results, M&E", flag: "🇬🇧" },
  { key: "fr", label: "Dossier complet (FR)", desc: "Document détaillé pour bailleurs francophones (AFD, GIZ, ministères RDC)", flag: "🇫🇷" },
  { key: "en", label: "Full Dossier (EN)", desc: "Detailed dossier for international funders (Gates, USAID, World Bank)", flag: "🇬🇧" },
  { key: "1page", label: "Pitch 1 page (FR)", desc: "Version condensée pour concours startup avec limite de pages", flag: "📄" },
  { key: "slides", label: "Pitch Deck 10 slides", desc: "Format diapositives prêt à copier dans Google Slides / PowerPoint", flag: "🎯" },
  { key: "lettre", label: "Lettre d'intention hôpital", desc: "Modèle à faire signer par le directeur d'un hôpital pilote (boost +60% candidatures)", flag: "✍️" },
  { key: "convention_cadre", label: "Convention-cadre hôpital partenaire", desc: "Contrat formel : obligations, tarifs, propriété des données, résiliation", flag: "⚖️" },
  { key: "convention_donnees", label: "Convention de traitement des données", desc: "Protection des données de santé, hébergement, droits des patients (DPA)", flag: "🔒" },
  { key: "partenariat_ministere", label: "Dossier partenariat Secrétariat Général à la Santé", desc: "Reconnaissance institutionnelle, DHIS2/SNIS, mise en relation DPS", flag: "🏛️" },
  { key: "deploiement_rdc", label: "Guide de déploiement local RDC", desc: "Docker + MinIO : héberger la plateforme sur un datacenter congolais", flag: "🖥️" },
];

export default function GrantDocument() {
  const [active, setActive] = useState("fr");
  const [content, setContent] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setContent("");
    axios.get(`${API}/pitch-document/raw?version=${active}`)
      .then((r) => setContent(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, [active]);

  const downloadMd = () => {
    const fileNames = {
      fr: "KWIKILA_Care_Dossier_Subventions_FR.md",
      en: "KWIKILA_Care_Grant_Application_EN.md",
      "1page": "KWIKILA_Care_Pitch_1_Page.md",
      slides: "KWIKILA_Care_Pitch_Deck_10_Slides.md",
      lettre: "KWIKILA_Care_Lettre_Intention_Hopital.md",
      emails: "KWIKILA_Care_Emails_5_Bailleurs.md",
      hopitaux: "KWIKILA_Care_Contacts_Hopitaux_Kinshasa.md",
      pilote: "KWIKILA_Care_Dossier_Technique_Financier_Pilote.md",
      pilote_en: "KWIKILA_Care_Technical_Financial_Proposal_Pilot_EN.md",
      convention_cadre: "KWIKILA_Care_Convention_Cadre_Hopital.md",
      convention_donnees: "KWIKILA_Care_Convention_Traitement_Donnees.md",
      partenariat_ministere: "KWIKILA_Care_Dossier_Partenariat_Ministere.md",
      deploiement_rdc: "KWIKILA_Care_Deploiement_Local_RDC.md",
    };
    const filename = fileNames[active] || "KWIKILA_Care.md";
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Téléchargement démarré (.md)");
  };

  const downloadPdf = () => {
    const url = `${API}/pitch-document/pdf?version=${active}`;
    window.open(url, "_blank");
    toast.success("Génération du PDF...");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success("Document copié");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Impossible de copier"); }
  };

  return (
    <div className="bg-white text-[#1A1D1A] min-h-screen">
      <div className="sticky top-0 z-50 bg-white border-b border-[#E5EBE7] px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <Link to="/" className="flex items-center gap-2" data-testid="grant-home">
          <div className="w-8 h-8 rounded-lg bg-[#1E4631] grid place-items-center">
            <HeartPulse className="w-4 h-4 text-white"/>
          </div>
          <span className="font-[Outfit] font-bold">KWIKILA Care</span>
          <span className="text-xs text-[#5C6B61] hidden sm:inline">· Dossier Subventions</span>
        </Link>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={downloadPdf} className="bg-[#D96C4A] hover:bg-[#B85A3D] text-white" data-testid="grant-pdf">
            <FileDown className="w-4 h-4 mr-2"/> Télécharger PDF
          </Button>
          <Button onClick={downloadMd} variant="outline" className="border-[#1E4631] text-[#1E4631]" data-testid="grant-md">
            <Download className="w-4 h-4 mr-2"/> Markdown (.md)
          </Button>
          <Button onClick={copyToClipboard} variant="outline" className="border-[#5C6B61] text-[#5C6B61]" data-testid="grant-copy">
            {copied ? <CheckCircle2 className="w-4 h-4 mr-2 text-[#2E7D32]"/> : <Copy className="w-4 h-4 mr-2"/>}
            {copied ? "Copié !" : "Copier"}
          </Button>
        </div>
      </div>

      <div className="bg-[#FDFBF7] border-b border-[#E5EBE7]">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-[Outfit] font-bold mb-4 inline-flex items-center gap-2">
            <Globe className="w-6 h-6 text-[#D96C4A]"/> Choisissez votre version
          </h1>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
            {VERSIONS.map((v) => (
              <button
                key={v.key}
                onClick={() => setActive(v.key)}
                data-testid={`version-${v.key}`}
                className={`text-left p-4 rounded-xl border-2 transition ${
                  active === v.key
                    ? "border-[#1E4631] bg-white shadow-md"
                    : "border-[#E5EBE7] bg-white/50 hover:border-[#1E4631]/50 hover:bg-white"
                }`}
              >
                <div className="text-2xl mb-1">{v.flag}</div>
                <div className="font-[Outfit] font-semibold text-[#1A1D1A]">{v.label}</div>
                <div className="text-xs text-[#5C6B61] mt-1 leading-relaxed">{v.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-[#1E4631]/5 border-b border-[#1E4631]/10 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-start gap-3 text-sm text-[#5C6B61]">
          <FileText className="w-5 h-5 mt-0.5 text-[#1E4631] flex-shrink-0"/>
          <div>
            <strong className="text-[#1A1D1A]">Comment utiliser :</strong> Le bouton <strong>PDF</strong> télécharge directement un dossier mis en page (à joindre à un email). Le bouton <strong>Markdown</strong> permet d'éditer dans Word/Google Docs. Personnalisez les sections marquées <code className="bg-white px-1 rounded text-xs">[À COMPLÉTER]</code> avec votre nom et votre équipe.
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        {!content ? (
          <div className="text-center text-[#5C6B61] py-20" data-testid="grant-loading">Chargement...</div>
        ) : (
          <pre className="whitespace-pre-wrap font-[Manrope] text-[15px] leading-relaxed text-[#1A1D1A]" data-testid="grant-content">
            {content}
          </pre>
        )}
      </div>
    </div>
  );
}
